import { useState } from "react";
import { Copy, Footprints, Gauge, Zap } from "lucide-react";
import { toast } from "sonner";
import { SPEEDS, setRouteSpeed, copyRoutes } from "@/lib/playOps";
import { routeTiming, playerRoutes, playerById } from "@/lib/playEngine";
import { getSport } from "@/sports";

const ICONS = { walk: Footprints, jog: Gauge, sprint: Zap };

export function SpeedChips({ play, route, edit }) {
  const legs = routeTiming(play, route).legs;
  const len = legs.reduce((s, l) => s + l.len, 0);
  const moving = legs.reduce((s, l) => s + (l.t1 - l.t0), 0);
  const cur = moving > 0 ? len / moving : 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`route-speed-${route.id}`}>
      <span className="text-xs font-bold uppercase tracking-wider text-slate-400 mr-1">Pace</span>
      {Object.entries(SPEEDS).map(([k, s]) => {
        const Icon = ICONS[k];
        const on = Math.abs(cur - s.yps) < 0.25 && !route.points.some((p) => p.dur > 0);
        return (
          <button key={k} type="button" aria-pressed={on} className={`chip ${on ? "chip-on" : ""}`} onClick={() => edit.set((p) => setRouteSpeed(p, route.id, s.yps))} data-testid={`speed-preset-${k}-${route.id}`}>
            <Icon size={14} />{s.label} <span className="opacity-70">{s.yps} yd/s</span>
          </button>
        );
      })}
    </div>
  );
}

export function CopyRoutes({ play, player, edit, locks }) {
  const cx = getSport(play.sport).centerX;
  const targets = play.players.filter((p) => p.id !== player.id && !locks[p.side]);
  const [to, setTo] = useState("");
  const target = playerById(play, to);
  const autoMirror = target ? (target.x - cx) * (player.x - cx) < 0 : true;
  const [mirror, setMirror] = useState(null);
  const useMirror = mirror ?? autoMirror;
  if (!playerRoutes(play, player.id).length || !targets.length) return null;
  const copy = () => {
    const had = playerRoutes(play, to).length;
    edit.set((p) => copyRoutes(p, player.id, to, useMirror));
    toast.success(`Route copied to ${target.label}${useMirror ? " (mirrored)" : ""}${had ? ". Their old route was replaced" : ""}. Undo to go back.`);
    setTo(""); setMirror(null);
  };
  return (
    <div className="rounded-xl border border-[#1F3358] p-3 space-y-2" data-testid="copy-routes">
      <p className="label mb-0 flex items-center gap-1"><Copy size={14} />Copy this route to another player</p>
      <select className="input" value={to} onChange={(e) => { setTo(e.target.value); setMirror(null); }} data-testid="copy-route-target">
        <option value="">Choose a player…</option>
        {targets.map((t) => <option key={t.id} value={t.id}>{t.label}{t.nickname ? ` · ${t.nickname}` : ""} ({t.side})</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm min-h-[44px] cursor-pointer">
        <input type="checkbox" className="w-5 h-5 accent-[#FF6B00]" checked={useMirror} onChange={(e) => setMirror(e.target.checked)} data-testid="copy-route-mirror" />
        Mirror for the other side of the field {target && mirror == null && <span className="text-xs text-slate-500">(auto: {autoMirror ? "other side" : "same side"})</span>}
      </label>
      <button className="btn btn-primary w-full" disabled={!to} onClick={copy} data-testid="btn-copy-route">Copy route{target ? ` to ${target.label}` : ""}</button>
      <p className="text-xs text-slate-500">Keeps the shape, timing, pauses and pace. Replaces the other player's current route.</p>
    </div>
  );
}
